import { Button, Card, CardProps, Popconfirm, Space, Tooltip, Typography } from 'antd'
import { CheckOutlined, DisconnectOutlined, FacebookOutlined } from '@ant-design/icons'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth'
import { useCheckFacebookSetup, useDisconnectFacebook, useRedirectFacebook } from '../hooks'
import { FACEBOOK_DISCONNECT } from '../../user/permission'
import { SetupCheckResult } from '../setup-check'
import { SetupCheckResults } from './setup-check-results'

export type ConnectionCardProps = {
    disconnect?: boolean
} & CardProps

export function ConnectionCard({ disconnect = true, ...props }: ConnectionCardProps) {
    const { activeCompany, can } = useAuth()
    const disconnectFacebookMutation = useDisconnectFacebook()
    const facebookRedirectMutation = useRedirectFacebook()
    const checkFacebookSetupMutation = useCheckFacebookSetup()
    const { t } = useTranslation()
    const [result, setResult] = useState<SetupCheckResult | null>(null)

    return (
        <Card
            className="w-full"
            {...props}
        >
            <Typography.Text
                type="secondary"
                className="h-[22px]"
                style={{ display: 'flex', justifyContent: 'space-between' }}
            >
                {t('Facebook')}
                {can(FACEBOOK_DISCONNECT) && activeCompany?.facebookConnected && disconnect && (
                    <Popconfirm
                        onConfirm={() => disconnectFacebookMutation.mutateAsync()}
                        title={t('Are you sure?')}
                        okText={t('Yes')}
                        cancelText={t('No')}
                        okButtonProps={{
                            danger: true,
                            loading: disconnectFacebookMutation.isPending,
                        }}
                    >
                        <Tooltip
                            placement="left"
                            title={t('Disconnect')}
                        >
                            <Button
                                size="small"
                                icon={<DisconnectOutlined />}
                            />
                        </Tooltip>
                    </Popconfirm>
                )}
            </Typography.Text>

            <Typography.Title
                level={3}
                className="mt-2"
            >
                {activeCompany?.facebookConnected ? (
                    <Space
                        size="small"
                        wrap
                    >
                        <Space size="small">
                            <CheckOutlined /> {t('Connected')}
                        </Space>
                        <Button
                            loading={checkFacebookSetupMutation.isPending}
                            onClick={() =>
                                checkFacebookSetupMutation.mutateAsync().then((checkResult) => setResult(checkResult))
                            }
                        >
                            {t('Check connection')}
                        </Button>
                    </Space>
                ) : (
                    <Button
                        type="primary"
                        loading={facebookRedirectMutation.isPending}
                        onClick={() => facebookRedirectMutation.mutateAsync().then((url) => window.open(url))}
                    >
                        <FacebookOutlined />
                        {t('Log in with Facebook')}
                    </Button>
                )}
            </Typography.Title>
            {result && (
                <SetupCheckResults
                    result={result}
                    successMessage={t('Facebook connection is working')}
                    warningMessage={t('Facebook connection needs attention')}
                />
            )}
        </Card>
    )
}
